import React, { useState, useEffect } from 'react';
import { Link, useLocation, Navigate, Outlet } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ThemeToggle } from '../../components/site/ThemeToggle';
import {
  LayoutDashboard, Image, FolderOpen, FileText, Building2,
  Settings, Mail, LogOut, Menu, X, Globe, User, Users, Server,
  BookOpen, Star, UsersRound, HelpCircle, Images, Palette,
  Package, CreditCard, Wallet, ChevronDown, SlidersHorizontal, Scale, AtSign, BarChart3
} from 'lucide-react';

const navItemsTop = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true, always: true },
  { to: '/admin/sliders', icon: Image, label: 'Slider Yönetimi', always: true },
  { to: '/admin/projects', icon: FolderOpen, label: 'Proje Yönetimi', menuKey: 'projeler' },
  { to: '/admin/catalogs', icon: FileText, label: 'E-Katalog', menuKey: 'ekatalog' },
  { to: '/admin/blog', icon: BookOpen, label: 'Haberler', menuKey: 'blog' },
  { to: '/admin/gallery', icon: Images, label: 'Galeri', menuKey: 'galeri' },
  { to: '/admin/testimonials', icon: Star, label: 'Referanslar', menuKey: 'referanslar' },
  { to: '/admin/team', icon: UsersRound, label: 'Ekip', menuKey: 'ekip' },
  { to: '/admin/faq', icon: HelpCircle, label: 'SSS', menuKey: 'sss' },
  { to: '/admin/services', icon: FolderOpen, label: 'Hizmetler', menuKey: 'hizmetler' },
  { to: '/admin/custom-pages', icon: FileText, label: 'Özel Sayfalar', always: true },
  { to: '/admin/legal-pages', icon: Scale, label: 'Yasal Sayfalar', always: true },
  { to: '/admin/corporate', icon: Building2, label: 'Kurumsal İçerik', menuKey: 'kurumsal' },
  { to: '/admin/members', icon: Users, label: 'Üyeler', requires: 'membership' },
  { to: '/admin/products', icon: Package, label: 'Ürün Yönetimi', requires: 'payments' },
  { to: '/admin/payments', icon: CreditCard, label: 'Ödeme Yönetimi', requires: 'payments' },
];

const settingsItems = [
  { to: '/admin/payment-settings', icon: Wallet, label: 'Ödeme Ayarları', requires: 'payments' },
  { to: '/admin/footer', icon: Settings, label: 'Genel & Footer', always: true },
  { to: '/admin/contact-settings', icon: Mail, label: 'İletişim Ayarları', menuKey: 'iletisim' },
  { to: '/admin/theme', icon: Palette, label: 'Tema & Görünüm', always: true },
  { to: '/admin/menu-settings', icon: LayoutDashboard, label: 'Menü Ayarları', always: true },
  { to: '/admin/seo', icon: Globe, label: 'SEO Ayarları', always: true },
  { to: '/admin/analytics', icon: BarChart3, label: 'Analytics / Takip', always: true },
  { to: '/admin/users', icon: Users, label: 'Kullanıcılar', always: true },
];

const navItemsBottom = [
  { to: '/admin/messages', icon: Mail, label: 'Mesajlar', always: true },
  { to: '/admin/newsletter', icon: AtSign, label: 'Bülten Aboneleri', always: true },
  { to: '/admin/profile', icon: User, label: 'Profilim', always: true },
];

const superAdminItems = [
  { to: '/admin/tenants', icon: Server, label: 'Tenant Yönetimi' },
];

export default function AdminLayout() {
  const { isAuthenticated, loading, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [userRole, setUserRole] = useState('');
  const [menuConfig, setMenuConfig] = useState(null);
  const [membership, setMembership] = useState({ membership_enabled: false, documents_enabled: false });
  const [paymentsEnabled, setPaymentsEnabled] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isVisible = (item) => {
    if (item.requires === 'membership') return membership.membership_enabled;
    if (item.requires === 'documents') return membership.documents_enabled;
    if (item.requires === 'payments') return paymentsEnabled;
    if (item.always) return true;
    if (!menuConfig) return true;
    if (item.menuKey && menuConfig[item.menuKey] === false) return false;
    return true;
  };

  const visibleSettings = settingsItems.filter(isVisible);
  const settingsChildActive = visibleSettings.some(i => location.pathname.startsWith(i.to));

  useEffect(() => {
    if (settingsChildActive) setSettingsOpen(true);
  }, [settingsChildActive]);

  useEffect(() => {
    import('../../lib/api').then(({ default: api }) => {
      api.get('/auth/me').then(r => setUserRole(r.data.role || '')).catch(() => {});
      api.get('/settings/menu').then(r => {
        const data = { ...r.data }; delete data.key;
        setMenuConfig(data);
      }).catch(() => {});
      api.get('/membership/status').then(r => setMembership({
        membership_enabled: !!r.data.membership_enabled,
        documents_enabled: !!r.data.documents_enabled,
      })).catch(() => {});
      api.get('/payments/config').then(r => setPaymentsEnabled(!!r.data.payments_enabled)).catch(() => {});
    });
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isAuthenticated) return <Navigate to="/admin/login" replace />;

  const isActive = (to, exact) => {
    if (exact) return location.pathname === to;
    return location.pathname.startsWith(to);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[260px_1fr]" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-200 lg:static lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex items-center justify-between h-16 px-4 border-b border-border">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-[hsl(var(--gold))] rounded-lg flex items-center justify-center">
              <span className="text-[hsl(var(--accent-foreground))] font-heading font-bold text-xs">I</span>
            </div>
            <span className="font-heading font-semibold text-sm">Yönetim</span>
          </Link>
          <button className="lg:hidden p-1" onClick={() => setSidebarOpen(false)}>
            <X className="w-5 h-5" />
          </button>
        </div>

        <nav className="p-3 space-y-1 overflow-y-auto h-[calc(100vh-4rem)]">
          {navItemsTop.filter(isVisible).map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive(item.to, item.exact)
                  ? 'bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] border-l-2 border-[hsl(var(--gold))]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
              data-testid="admin-sidebar-nav-item"
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}

          {navItemsBottom.filter(isVisible).map(item => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                isActive(item.to, item.exact)
                  ? 'bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] border-l-2 border-[hsl(var(--gold))]'
                  : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
              }`}
              data-testid="admin-sidebar-nav-item"
            >
              <item.icon className="w-4 h-4" />
              {item.label}
            </Link>
          ))}

          {/* Settings collapsible group (en altta) */}
          {visibleSettings.length > 0 && (
            <div>
              <button
                type="button"
                onClick={() => setSettingsOpen(o => !o)}
                aria-expanded={settingsOpen}
                className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                  settingsChildActive
                    ? 'text-[hsl(var(--gold))]'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
                data-testid="admin-sidebar-settings-group"
              >
                <SlidersHorizontal className="w-4 h-4" />
                <span className="flex-1 text-left">Ayarlar</span>
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${settingsOpen ? 'rotate-180' : ''}`} />
              </button>
              {settingsOpen && (
                <div className="mt-1 ml-3 pl-3 border-l border-border space-y-1" data-testid="admin-sidebar-settings-submenu">
                  {visibleSettings.map(item => (
                    <Link
                      key={item.to}
                      to={item.to}
                      onClick={() => setSidebarOpen(false)}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                        isActive(item.to, item.exact)
                          ? 'bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] border-l-2 border-[hsl(var(--gold))]'
                          : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                      }`}
                      data-testid="admin-sidebar-nav-item"
                    >
                      <item.icon className="w-4 h-4" />
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          )}


          {/* Super Admin Items */}
          {userRole === 'superadmin' && (
            <div className="pt-3 mt-3 border-t border-border">
              <div className="px-3 py-1 text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Süper Admin</div>
              {superAdminItems.map(item => (
                <Link
                  key={item.to}
                  to={item.to}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors duration-200 ${
                    isActive(item.to, item.exact)
                      ? 'bg-[hsl(var(--gold)/0.1)] text-[hsl(var(--gold))] border-l-2 border-[hsl(var(--gold))]'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
            </div>
          )}

          <div className="pt-4 mt-4 border-t border-border">
            <Link
              to="/"
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
            >
              <Globe className="w-4 h-4" /> Siteyi Gör
            </Link>
            <button
              onClick={logout}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium text-destructive hover:bg-destructive/10 transition-colors w-full"
              data-testid="admin-logout-button"
            >
              <LogOut className="w-4 h-4" /> Çıkış Yap
            </button>
          </div>
        </nav>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-40 bg-black/50 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Main Content */}
      <div className="flex flex-col min-h-screen min-w-0">
        {/* Topbar */}
        <header className="sticky top-0 z-30 flex items-center justify-between h-16 px-4 md:px-6 border-b border-border bg-background/95 backdrop-blur">
          <button className="lg:hidden p-2 rounded-lg hover:bg-secondary" onClick={() => setSidebarOpen(true)}>
            <Menu className="w-5 h-5" />
          </button>
          <div className="flex-1" />
          <div className="flex items-center gap-2">
            <ThemeToggle />
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-4 md:p-6">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
